import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "./ui/button";
import { Loader2, MapPin, Target, Check, X } from "lucide-react";

// Fix default marker icons so they render inside bundlers (Webpack/CRA).
// Leaflet's default assets reference images relative to the CSS file, which
// breaks when the icons are code-split; we point to the hosted CDN versions.
L.Icon.Default.mergeOptions({
    iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
    iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
    shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Default view when there's no existing pin and no geolocation permission —
// center on Riyadh (the Car Learn account's home city).
const DEFAULT_CENTER = [24.7136, 46.6753];
const DEFAULT_ZOOM = 12;

/**
 * Reverse-geocode (lat,lng) → human-readable area using OpenStreetMap's
 * Nominatim service. Free + no API key but rate-limited to ~1 req/sec.
 */
async function reverseGeocode(lat, lng) {
    try {
        const r = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=ar`,
            { headers: { Accept: "application/json" } }
        );
        if (!r.ok) return null;
        const j = await r.json();
        return j.display_name || null;
    } catch {
        return null;
    }
}

/**
 * Fullscreen dialog with a Leaflet map. User can drag the pin or click
 * anywhere to drop it. "تحديد موقعي الحالي" uses navigator.geolocation.
 * On confirm, calls onConfirm({ lat, lng, address }) — address is the
 * reverse-geocoded display name (may be null if the lookup failed).
 */
export default function LocationPickerDialog({
    open,
    onClose,
    initialLat,
    initialLng,
    initialText,
    onConfirm,
}) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const [coords, setCoords] = useState(null);
    const [address, setAddress] = useState(initialText || "");
    const [resolving, setResolving] = useState(false);
    const [locating, setLocating] = useState(false);

    // Initialise the map once the dialog opens.
    useEffect(() => {
        if (!open) return undefined;
        // defer map init to the next tick so the container has a size
        const timer = setTimeout(() => {
            if (!containerRef.current || mapRef.current) return;
            const startLat = initialLat ?? DEFAULT_CENTER[0];
            const startLng = initialLng ?? DEFAULT_CENTER[1];
            const startZoom = initialLat != null && initialLng != null ? 15 : DEFAULT_ZOOM;
            const map = L.map(containerRef.current, { zoomControl: true }).setView(
                [startLat, startLng],
                startZoom,
            );
            L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                maxZoom: 19,
                attribution: "© OpenStreetMap contributors",
            }).addTo(map);
            const marker = L.marker([startLat, startLng], { draggable: true }).addTo(map);
            marker.on("dragend", () => {
                const { lat, lng } = marker.getLatLng();
                setCoords({ lat, lng });
                lookupAddress(lat, lng);
            });
            map.on("click", (e) => {
                const { lat, lng } = e.latlng;
                marker.setLatLng([lat, lng]);
                setCoords({ lat, lng });
                lookupAddress(lat, lng);
            });
            mapRef.current = map;
            markerRef.current = marker;
            if (initialLat != null && initialLng != null) {
                setCoords({ lat: initialLat, lng: initialLng });
            }
            // Force resize once the dialog layout settles.
            setTimeout(() => map.invalidateSize(), 150);
        }, 50);
        return () => {
            clearTimeout(timer);
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
                markerRef.current = null;
            }
        };
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const lookupAddress = async (lat, lng) => {
        setResolving(true);
        const name = await reverseGeocode(lat, lng);
        setResolving(false);
        if (name) setAddress(name);
    };

    const locateMe = () => {
        if (!navigator.geolocation) return;
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                if (markerRef.current && mapRef.current) {
                    markerRef.current.setLatLng([latitude, longitude]);
                    mapRef.current.setView([latitude, longitude], 16);
                }
                setCoords({ lat: latitude, lng: longitude });
                lookupAddress(latitude, longitude);
                setLocating(false);
            },
            () => setLocating(false),
            { enableHighAccuracy: true, timeout: 10000 },
        );
    };

    const confirm = () => {
        if (!coords) return;
        onConfirm({ lat: coords.lat, lng: coords.lng, address: address || null });
    };

    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-3 py-6"
            data-testid="loc-picker-dialog"
            onClick={onClose}
        >
            <div
                className="flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
                style={{ maxHeight: "90vh" }}
                dir="rtl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-neutral-200 bg-neutral-50 px-5 py-3">
                    <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-amber-700" />
                        <span className="font-heading text-base font-bold">تحديد الموقع على الخريطة</span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="إغلاق"
                        className="rounded-full p-1 hover:bg-neutral-200"
                        data-testid="loc-picker-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div
                    ref={containerRef}
                    data-testid="loc-picker-map"
                    className="h-[55vh] w-full"
                />

                <div className="space-y-3 border-t border-neutral-200 bg-white px-5 py-3">
                    <div className="flex items-center justify-between gap-2 text-xs">
                        <div className="flex-1 text-neutral-600" data-testid="loc-picker-address">
                            {resolving ? (
                                <span className="inline-flex items-center gap-1 text-neutral-500">
                                    <Loader2 className="h-3 w-3 animate-spin" /> يتم جلب اسم المنطقة...
                                </span>
                            ) : address ? (
                                <span className="line-clamp-2">{address}</span>
                            ) : (
                                <span className="text-neutral-400">اضغط على الخريطة أو اسحب العلامة لتحديد الموقع</span>
                            )}
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={locateMe}
                            disabled={locating}
                            data-testid="loc-picker-me-btn"
                            className="shrink-0 gap-1"
                        >
                            {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Target className="h-3.5 w-3.5" />}
                            موقعي
                        </Button>
                    </div>
                    {coords ? (
                        <div className="font-mono text-[11px] text-neutral-500" dir="ltr" data-testid="loc-picker-coords">
                            {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
                        </div>
                    ) : null}
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose} className="flex-1" data-testid="loc-picker-cancel">
                            إلغاء
                        </Button>
                        <Button
                            onClick={confirm}
                            disabled={!coords}
                            className="flex-1 gap-2 bg-amber-700 text-white hover:bg-amber-800"
                            data-testid="loc-picker-confirm"
                        >
                            <Check className="h-4 w-4" />
                            تأكيد الموقع
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Read-only viewer — opens when the user clicks a saved location chip on
 * a booking row. Shows the pin on the map without allowing edits.
 */
export function LocationViewerDialog({ open, onClose, lat, lng, address }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const timer = setTimeout(() => {
            if (!containerRef.current || mapRef.current) return;
            const map = L.map(containerRef.current, { zoomControl: true }).setView([lat, lng], 16);
            L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
                maxZoom: 19,
                attribution: "© OpenStreetMap contributors",
            }).addTo(map);
            L.marker([lat, lng]).addTo(map);
            mapRef.current = map;
            setTimeout(() => map.invalidateSize(), 150);
        }, 50);
        return () => {
            clearTimeout(timer);
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [open, lat, lng]);

    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-3 py-6"
            data-testid="loc-viewer-dialog"
            onClick={onClose}
        >
            <div
                className="flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
                style={{ maxHeight: "90vh" }}
                dir="rtl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-neutral-200 bg-neutral-50 px-5 py-3">
                    <div className="flex items-center gap-2">
                        <MapPin className="h-5 w-5 text-amber-700" />
                        <span className="font-heading text-base font-bold">موقع العميل</span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="إغلاق"
                        className="rounded-full p-1 hover:bg-neutral-200"
                        data-testid="loc-viewer-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div ref={containerRef} data-testid="loc-viewer-map" className="h-[60vh] w-full" />
                {address ? (
                    <div className="border-t border-neutral-200 bg-white px-5 py-3 text-xs text-neutral-600" data-testid="loc-viewer-address">
                        {address}
                    </div>
                ) : null}
            </div>
        </div>
    );
}
